import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User, Eye, EyeOff, Loader2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { API } from "../App";
import useExperiment from "../hooks/useExperiment";

const AuthModal = ({ isOpen, onClose, onSuccess, defaultTab = "login", message }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  // A/B Testing: Signup social proof experiment
  const { 
    variant: signupVariant, 
    config: signupConfig,
    trackConversion: trackSignupConversion,
    isInExperiment: isInSignupExperiment 
  } = useExperiment("signup_social_proof");

  const validateForm = () => {
    const newErrors = {};
    
    if (activeTab === "register" && !formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[A-Za-z]/.test(formData.password)) {
      newErrors.password = "Password must contain at least one letter";
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = "Password must contain at least one number";
    }
    
    if (activeTab === "register" && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const endpoint = activeTab === "login" ? "/auth/login" : "/auth/register";
      
      // For registration, check for referral code
      const referralCode = localStorage.getItem("referral_code");
      
      const body = activeTab === "login" 
        ? { email: formData.email, password: formData.password }
        : { 
            name: formData.name, 
            email: formData.email, 
            password: formData.password,
            referral_code: referralCode || undefined
          };
      
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      
      // Clone response to avoid "body stream already read" error
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error("Server returned invalid response");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Authentication failed");
      }
      
      // Store session token in localStorage as backup
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
      }
      
      // Clear referral code after successful registration
      if (activeTab === "register" && referralCode) {
        localStorage.removeItem("referral_code");
      }
      
      toast.success(activeTab === "login" ? "Welcome back!" : "Account created successfully!");
      
      // Track A/B test conversion for registration
      if (activeTab === "register" && isInSignupExperiment) {
        trackSignupConversion("registration_complete", { variant: signupVariant });
      }
      
      // Close modal first, then call success callback
      // This ensures navigation happens after modal is fully closed
      onClose();
      
      // Use setTimeout to ensure modal unmount completes before navigation
      setTimeout(() => {
        onSuccess(data.user);
      }, 100);
      
    } catch (error) {
      toast.error(error.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use Rekrutor's own Google OAuth (shows "Rekrutor" branding on consent screen)
    try {
      const res = await fetch(`${API}/auth/google/url?origin=${encodeURIComponent(window.location.origin)}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        toast.error("Failed to connect to Google. Please try again.");
      }
    } catch {
      toast.error("Failed to connect to Google. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="auth-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-obsidian-light border border-[#333] w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
          data-testid="auth-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#333]">
            <h2 className="font-heading text-xl font-bold">
              {activeTab === "login" ? "Welcome Back" : "Create Account"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="auth-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Custom message */}
          {message && (
            <div className="mx-6 mt-4 p-3 bg-crimson/10 border border-crimson/20 text-center">
              <p className="text-sm text-crimson font-medium">{message}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-[#333]">
            <button
              onClick={() => setActiveTab("login")}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === "login"
                  ? "text-crimson border-b-2 border-crimson"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid="auth-tab-login"
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab("register")}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === "register"
                  ? "text-crimson border-b-2 border-crimson"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid="auth-tab-register"
            >
              Create Account
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Social Proof Banner - A/B Test */}
            {signupConfig?.show_social_proof && activeTab === "register" && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 mb-2">
                <Users className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span className="text-sm text-green-400">{signupConfig.proof_text}</span>
              </div>
            )}
            
            {/* Google Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full border-[#333] hover:border-white/50 bg-white text-black hover:bg-gray-100"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#333]" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-obsidian-light px-2 text-gray-500">Or continue with email</span>
              </div>
            </div>

            {/* Name field (register only) */}
            {activeTab === "register" && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-obsidian border border-[#333] text-white py-2 pl-10 pr-4 focus:border-crimson outline-none"
                    placeholder="John Doe"
                    data-testid="auth-name-input"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
            )}

            {/* Email field */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-obsidian border border-[#333] text-white py-2 pl-10 pr-4 focus:border-crimson outline-none"
                  placeholder="you@example.com"
                  data-testid="auth-email-input"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password field */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-obsidian border border-[#333] text-white py-2 pl-10 pr-10 focus:border-crimson outline-none"
                  placeholder="********"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              {activeTab === "register" && !errors.password && (
                <p className="text-gray-500 text-xs mt-1">Min 8 characters with letters and numbers</p>
              )}
            </div>

            {/* Forgot Password (login only) */}
            {activeTab === "login" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.email) { toast.error("Enter your email first"); return; }
                    try {
                      const res = await fetch(`${API}/auth/forgot-password`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: formData.email })
                      });
                      const data = await res.json();
                      toast.success(data.message || "Reset link sent if account exists");
                    } catch { toast.error("Something went wrong"); }
                  }}
                  className="text-xs text-crimson hover:underline"
                  data-testid="forgot-password-btn"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {/* Confirm Password (register only) */}
            {activeTab === "register" && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full bg-obsidian border border-[#333] text-white py-2 pl-10 pr-4 focus:border-crimson outline-none"
                    placeholder="********"
                    data-testid="auth-confirm-password-input"
                  />
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-crimson hover:bg-crimson-hover text-white"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {activeTab === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          {/* Footer */}
          <div className="px-6 pb-6 text-center text-sm text-gray-500">
            {activeTab === "login" ? (
              <p>
                Don't have an account?{" "}
                <button
                  onClick={() => setActiveTab("register")}
                  className="text-crimson hover:underline"
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p>
                Already have an account?{" "}
                <button
                  onClick={() => setActiveTab("login")}
                  className="text-crimson hover:underline"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;
